import { AxiosResponse } from "axios";
import { takeLatest, call, put, all } from "redux-saga/effects";
import {
    getDashboardSummary as getDashboardSummaryService,
    getDashboardTenants as getDashboardTenantsService,
    getDashboardEvents as getDashboardEventsService,
    getDashboardTrends as getDashboardTrendsService,
    getTenantSummary as getTenantSummaryService,
    getTenantEvents as getTenantEventsService,
    getTenantTrends as getTenantTrendsService,
    getEventRecords as getEventRecordsService,
} from "../../services/analytics/analytics.service";
import { getOrganizations } from "../../services/organization/organization.service";
import {
    getAllOrganizationsForAnalytics,
    getAllOrganizationsForAnalyticsSuccess,
    getAllOrganizationsForAnalyticsFailure,
    getDashboardSummary,
    getDashboardSummarySuccess,
    getDashboardSummaryFailure,
    getDashboardTenants,
    getDashboardTenantsSuccess,
    getDashboardTenantsFailure,
    getDashboardEvents,
    getDashboardEventsSuccess,
    getDashboardEventsFailure,
    getDashboardTrends,
    getDashboardTrendsSuccess,
    getDashboardTrendsFailure,
    getTenantSummary,
    getTenantSummarySuccess,
    getTenantSummaryFailure,
    getTenantEvents,
    getTenantEventsSuccess,
    getTenantEventsFailure,
    getTenantTrends,
    getTenantTrendsSuccess,
    getTenantTrendsFailure,
    getEventRecords,
    getEventRecordsSuccess,
    getEventRecordsFailure,
    resetEventRecords,
} from "../reducer/analytics";
import {
    GetDashboardAction,
    GetEventRecordsAction,
    GetTenantAction,
    ResetEventRecordsAction,
} from "../types/analytics";
import {
    EventRecordProperties,
    IAnalyticsSummaryResponse,
    IEventRecord,
    IEventRecordsPagination,
    IEventRecordsQuery,
    IEventRecordsResponse,
    ITenantRanking,
    ITopEvent,
    ITrendPoint,
} from "../../interfaces/analytics.interface";

const ORGS_PER_PAGE = 200;

const toNumber = (value: unknown): number => {
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    if (typeof value === "string" && value.trim() !== "") {
        const n = Number(value);
        return Number.isFinite(n) ? n : 0;
    }
    return 0;
};

// Accepts a raw array or any object that wraps the array under `data`
// (or nested further). Returns the first array it finds, else [].
const extractArray = (raw: unknown): unknown[] => {
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === "object") {
        const candidate = (raw as any).data;
        if (Array.isArray(candidate)) return candidate;
        if (candidate && typeof candidate === "object") {
            return extractArray(candidate);
        }
    }
    return [];
};

// Accepts either { summary, platforms } or { data: { summary, platforms } }
const extractSummaryRaw = (raw: unknown): any => {
    if (!raw || typeof raw !== "object") return {};
    const obj = raw as any;
    if (Array.isArray(obj.summary) || Array.isArray(obj.platforms)) return obj;
    if (obj.data && typeof obj.data === "object") return obj.data;
    return obj;
};

const normalizeSummary = (raw: any): IAnalyticsSummaryResponse => ({
    summary: Array.isArray(raw?.summary)
        ? raw.summary.map((s: any) => ({
              total_events: toNumber(s?.total_events),
              unique_users: toNumber(s?.unique_users),
          }))
        : [],
    platforms: Array.isArray(raw?.platforms)
        ? raw.platforms.map((p: any) => ({
              platform: String(p?.platform ?? ""),
              count: toNumber(p?.count),
          }))
        : [],
});

const normalizeTenantRanking = (rows: unknown): ITenantRanking[] =>
    Array.isArray(rows)
        ? rows.map((r: any) => ({
              tenant_id: String(r?.tenant_id ?? ""),
              total_events: toNumber(r?.total_events),
              unique_users: toNumber(r?.unique_users),
          }))
        : [];

const normalizeTopEvents = (rows: unknown): ITopEvent[] =>
    Array.isArray(rows)
        ? rows.map((r: any) => ({
              event_name: String(r?.event_name ?? ""),
              count: toNumber(r?.count),
          }))
        : [];

// Like toNumber, but tells "missing / unparseable" (null) apart from 0
const toNullableNumber = (value: unknown): number | null => {
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value === "string" && value.trim() !== "") {
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
    }
    return null;
};

const toNullableString = (value: unknown): string | null => {
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }
    return null;
};

const toPositiveInt = (value: unknown, fallback: number): number => {
    const n = toNullableNumber(value);
    return n !== null && n >= 1 ? Math.floor(n) : fallback;
};

// Accepts { records, pagination } or any object wrapping it under `data`
const extractEventRecordsRaw = (raw: unknown): any => {
    if (!raw || typeof raw !== "object") return {};
    const obj = raw as any;
    if (
        Array.isArray(obj.records) ||
        (obj.pagination && typeof obj.pagination === "object")
    ) {
        return obj;
    }
    if (obj.data && typeof obj.data === "object") {
        return extractEventRecordsRaw(obj.data);
    }
    return obj;
};

const normalizeProperties = (value: unknown): EventRecordProperties => {
    if (value === null || value === undefined) return null;
    if (Array.isArray(value)) return value;
    if (typeof value === "object") return value as Record<string, unknown>;
    if (typeof value === "string") return value;
    return String(value);
};

const normalizeEventRecord = (r: any): IEventRecord => ({
    tenant_id: toNullableNumber(r?.tenant_id) ?? 0,
    user_id: toNullableNumber(r?.user_id),
    event_name: String(r?.event_name ?? ""),
    platform: toNullableString(r?.platform),
    store_id:
        typeof r?.store_id === "string" ||
        (typeof r?.store_id === "number" && Number.isFinite(r.store_id))
            ? r.store_id
            : null,
    anonymous_id: toNullableString(r?.anonymous_id),
    session_id: toNullableString(r?.session_id),
    app_version: toNullableString(r?.app_version),
    product_id: toNullableString(r?.product_id),
    event_timestamp: String(r?.event_timestamp ?? ""),
    properties: normalizeProperties(r?.properties),
});

const normalizeEventRecords = (
    raw: any,
    query: IEventRecordsQuery
): IEventRecordsResponse => {
    const rows: unknown[] = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.records)
          ? raw.records
          : [];
    const records = rows.map(normalizeEventRecord);
    const p = raw?.pagination;

    const page = toPositiveInt(p?.page, query.page);
    const perPage = toPositiveInt(p?.per_page, query.perPage);
    const rawTotal = toNullableNumber(p?.total);
    // No total from the API: the rows we hold are the best lower bound
    const total =
        rawTotal !== null && rawTotal >= 0
            ? Math.floor(rawTotal)
            : (page - 1) * perPage + records.length;
    const pagination: IEventRecordsPagination = {
        page,
        per_page: perPage,
        total,
        last_page: toPositiveInt(
            p?.last_page,
            Math.max(1, Math.ceil(total / perPage))
        ),
    };
    // Snapshot of this query, sent back verbatim on its later pages. Left out
    // when absent or blank so those pages ask without one, as before.
    if (typeof p?.as_of === "string" && p.as_of.trim() !== "") {
        pagination.as_of = p.as_of;
    }

    return { records, pagination };
};

const normalizeTrends = (rows: unknown): ITrendPoint[] =>
    Array.isArray(rows)
        ? rows.map((r: any) => ({
              year: toNumber(r?.year),
              month: toNumber(r?.month),
              day: toNumber(r?.day),
              total_events: toNumber(r?.total_events),
              unique_users: toNumber(r?.unique_users),
          }))
        : [];

function* getAllOrganizationsForAnalyticsSaga() {
    try {
        const firstPage: AxiosResponse = yield call(getOrganizations, {
            search: "",
            perPage: ORGS_PER_PAGE,
            page: 1,
        });
        const firstData = firstPage.data?.data ?? [];
        const pagesTotal: number = firstPage.data?.meta?.pages_total ?? 1;

        let allRows: Array<{ id: string | number; attributes: { name: string } }> = [
            ...firstData,
        ];

        if (pagesTotal > 1) {
            const pageNumbers = Array.from({ length: pagesTotal - 1 }, (_, i) => i + 2);
            const responses: AxiosResponse[] = yield all(
                pageNumbers.map((page) =>
                    call(getOrganizations, { search: "", perPage: ORGS_PER_PAGE, page })
                )
            );
            for (const res of responses) {
                allRows = allRows.concat(res.data?.data ?? []);
            }
        }

        const map: Record<string, string> = {};
        for (const row of allRows) {
            map[String(row.id)] = row.attributes?.name ?? `Tenant #${row.id}`;
        }
        yield put(getAllOrganizationsForAnalyticsSuccess(map));
    } catch (_error) {
        yield put(getAllOrganizationsForAnalyticsFailure());
    }
}

function* getDashboardSummarySaga(action: GetDashboardAction) {
    try {
        const { data }: AxiosResponse = yield call(getDashboardSummaryService, action.payload);
        yield put(getDashboardSummarySuccess(normalizeSummary(extractSummaryRaw(data))));
    } catch (_error) {
        yield put(getDashboardSummaryFailure());
    }
}

function* getDashboardTenantsSaga(action: GetDashboardAction) {
    try {
        const { data }: AxiosResponse = yield call(getDashboardTenantsService, action.payload);
        yield put(getDashboardTenantsSuccess(normalizeTenantRanking(extractArray(data))));
    } catch (_error) {
        yield put(getDashboardTenantsFailure());
    }
}

function* getDashboardEventsSaga(action: GetDashboardAction) {
    try {
        const { data }: AxiosResponse = yield call(getDashboardEventsService, action.payload);
        yield put(getDashboardEventsSuccess(normalizeTopEvents(extractArray(data))));
    } catch (_error) {
        yield put(getDashboardEventsFailure());
    }
}

function* getDashboardTrendsSaga(action: GetDashboardAction) {
    try {
        const { data }: AxiosResponse = yield call(getDashboardTrendsService, action.payload);
        yield put(getDashboardTrendsSuccess(normalizeTrends(extractArray(data))));
    } catch (_error) {
        yield put(getDashboardTrendsFailure());
    }
}

function* getTenantSummarySaga(action: GetTenantAction) {
    try {
        const { data }: AxiosResponse = yield call(
            getTenantSummaryService,
            action.payload.tenantId,
            action.payload.range
        );
        yield put(getTenantSummarySuccess(normalizeSummary(extractSummaryRaw(data))));
    } catch (_error) {
        yield put(getTenantSummaryFailure());
    }
}

function* getTenantEventsSaga(action: GetTenantAction) {
    try {
        const { data }: AxiosResponse = yield call(
            getTenantEventsService,
            action.payload.tenantId,
            action.payload.range
        );
        yield put(getTenantEventsSuccess(normalizeTopEvents(extractArray(data))));
    } catch (_error) {
        yield put(getTenantEventsFailure());
    }
}

function* getTenantTrendsSaga(action: GetTenantAction) {
    try {
        const { data }: AxiosResponse = yield call(
            getTenantTrendsService,
            action.payload.tenantId,
            action.payload.range
        );
        yield put(getTenantTrendsSuccess(normalizeTrends(extractArray(data))));
    } catch (_error) {
        yield put(getTenantTrendsFailure());
    }
}

// Also taken on resetEventRecords: takeLatest then cancels the fetch still in
// flight, so its response cannot refill the slice after the page unmounted.
function* getEventRecordsSaga(
    action: GetEventRecordsAction | ResetEventRecordsAction
) {
    if (!getEventRecords.match(action)) return;
    try {
        const { data }: AxiosResponse = yield call(
            getEventRecordsService,
            action.payload
        );
        yield put(
            getEventRecordsSuccess(
                normalizeEventRecords(
                    extractEventRecordsRaw(data),
                    action.payload
                )
            )
        );
    } catch (_error) {
        yield put(getEventRecordsFailure());
    }
}

export function* rootSaga() {
    yield takeLatest(getAllOrganizationsForAnalytics.type, getAllOrganizationsForAnalyticsSaga);
    yield takeLatest(getDashboardSummary.type, getDashboardSummarySaga);
    yield takeLatest(getDashboardTenants.type, getDashboardTenantsSaga);
    yield takeLatest(getDashboardEvents.type, getDashboardEventsSaga);
    yield takeLatest(getDashboardTrends.type, getDashboardTrendsSaga);
    yield takeLatest(getTenantSummary.type, getTenantSummarySaga);
    yield takeLatest(getTenantEvents.type, getTenantEventsSaga);
    yield takeLatest(getTenantTrends.type, getTenantTrendsSaga);
    yield takeLatest(
        [getEventRecords.type, resetEventRecords.type],
        getEventRecordsSaga
    );
}
